/**
 * ETL v2 — Expression evaluator (pure interpreter).
 *
 * Walks an {@link Expr} AST against an {@link EvalEnv}. No `eval`, no
 * `Function`, no prototype walking. Member access is delegated to the
 * env's `resolveMember` so the host can enforce its own allow-list of
 * safe object shapes (e.g. `UserView`, `TenantView`, `error`).
 *
 * The evaluator is total in shape — it never throws JS errors that
 * weren't intentionally raised; type mismatches surface as
 * {@link ExprEvalError}s the runtime can convert into `AssertionError`
 * or `ScopeError` depending on context.
 *
 * **Strictness** is opt-in via {@link EvalEnv.strict}. In loose mode
 * (default), undefined identifiers, undefined members, and arithmetic
 * type mismatches resolve to `undefined`/coerced values — the same
 * forgiving semantics that JS template literals exhibit. Strict mode
 * throws on every such miss.
 */

import {
  TOP_LEVEL_IDENTS,
  ExprEvalError,
  type Expr,
  type ExprBuiltinFn,
  type ExprBinaryOp,
} from './expr-types';

/**
 * Host-supplied evaluation environment. All identifiers, member walks,
 * and builtin calls are dispatched through this interface so the
 * portable package never reaches into host state directly.
 */
export interface EvalEnv {
  /** Strict mode: throw on undefined identifier / undefined member. Default false. */
  readonly strict?: boolean;

  /**
   * Resolves a top-level identifier. Allowed names are the members of
   * {@link TOP_LEVEL_IDENTS}. The evaluator pre-validates membership
   * before calling this hook.
   *
   * Returns `undefined` if the name is unbound; in strict mode the
   * evaluator converts that to {@link ExprEvalError}.
   */
  resolveTopLevel(name: string): unknown;

  /**
   * Resolves `parent[prop]` for member or index access. Receives the
   * already-evaluated parent value; the env chooses whether to expose
   * the property (allow-list) or return `undefined`.
   *
   * Hosts MUST NOT walk JS prototypes here. The recommended impl is a
   * static map of well-known parent shapes.
   */
  resolveMember(parent: unknown, prop: string): unknown;

  /**
   * Invokes a whitelisted builtin function. The evaluator pre-validates
   * `fn` membership in {@link EXPR_BUILTIN_FNS}; arity is checked at
   * parse time.
   *
   * Throwing {@link ExprEvalError} from here is allowed for runtime
   * errors (e.g. `count()` called with a non-locator).
   */
  callBuiltin(fn: ExprBuiltinFn, args: ReadonlyArray<unknown>): unknown;
}

/* ──────────────────────────── operators ────────────────────────────── */

function looseEq(a: unknown, b: unknown): boolean {
  // Strict equal first — fast path, handles same-type and reference cases.
  if (a === b) return true;
  // null / undefined cross-equate (mirrors JS `==` for these only).
  if (a == null && b == null) return true;
  // Numeric coercion: only if both sides are number-like.
  if (typeof a === 'number' && typeof b === 'string') return a === Number(b);
  if (typeof a === 'string' && typeof b === 'number') return Number(a) === b;
  if (typeof a === 'boolean') return looseEq(a ? 1 : 0, b);
  if (typeof b === 'boolean') return looseEq(a, b ? 1 : 0);
  return false;
}

function numericCmp(op: '<' | '<=' | '>' | '>=', a: unknown, b: unknown): boolean {
  const la = toNumber(a);
  const lb = toNumber(b);
  if (Number.isNaN(la) || Number.isNaN(lb)) return false;
  switch (op) {
    case '<':  return la <  lb;
    case '<=': return la <= lb;
    case '>':  return la >  lb;
    case '>=': return la >= lb;
  }
}

function toNumber(v: unknown): number {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') return Number(v);
  if (typeof v === 'boolean') return v ? 1 : 0;
  if (v == null) return 0;
  return Number.NaN;
}

function arith(op: '+' | '-' | '*' | '/' | '%', a: unknown, b: unknown): number | string {
  if (op === '+') {
    if (typeof a === 'string' || typeof b === 'string') {
      return String(a ?? '') + String(b ?? '');
    }
    return toNumber(a) + toNumber(b);
  }
  const na = toNumber(a);
  const nb = toNumber(b);
  switch (op) {
    case '-': return na - nb;
    case '*': return na * nb;
    case '/': return nb === 0 ? Number.NaN : na / nb;
    case '%': return nb === 0 ? Number.NaN : na % nb;
  }
}

function truthy(v: unknown): boolean {
  if (v == null) return false;
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0 && !Number.isNaN(v);
  if (typeof v === 'string') return v.length > 0;
  if (Array.isArray(v)) return v.length > 0;
  return true;
}

/* ──────────────────────────── interpreter ──────────────────────────── */

export function evalExpr(expr: Expr, env: EvalEnv): unknown {
  switch (expr.kind) {
    case 'lit':
      return expr.value;

    case 'ident':
      return resolveIdent(expr.name, env);

    case 'tpl': {
      let out = '';
      for (const p of expr.parts) {
        if (typeof p === 'string') {
          out += p;
        } else {
          const v = evalExpr(p, env);
          out += v == null ? '' : String(v);
        }
      }
      return out;
    }

    case 'regex':
      return new RegExp(expr.source, expr.flags);

    case 'locator':
      return expr.locator;

    case 'member': {
      const parent = evalExpr(expr.obj, env);
      if (parent == null) {
        if (env.strict) {
          throw new ExprEvalError(`cannot read "${expr.prop}" of null/undefined`);
        }
        return undefined;
      }
      const v = env.resolveMember(parent, expr.prop);
      if (v === undefined && env.strict) {
        throw new ExprEvalError(`undefined member "${expr.prop}"`);
      }
      return v;
    }

    case 'index': {
      const parent = evalExpr(expr.obj, env);
      if (parent == null) {
        if (env.strict) {
          throw new ExprEvalError('cannot index null/undefined');
        }
        return undefined;
      }
      const key = evalExpr(expr.key, env);
      const prop = typeof key === 'string' || typeof key === 'number'
        ? String(key)
        : '';
      if (Array.isArray(parent) && /^-?\d+$/.test(prop)) {
        return parent[Number(prop)];
      }
      const v = env.resolveMember(parent, prop);
      if (v === undefined && env.strict) {
        throw new ExprEvalError(`undefined index "${prop}"`);
      }
      return v;
    }

    case 'unary': {
      const a = evalExpr(expr.arg, env);
      if (expr.op === '!') return !truthy(a);
      // unary minus
      return -toNumber(a);
    }

    case 'binary':
      return evalBinary(expr.op, expr.left, expr.right, env);

    case 'call': {
      const args = expr.args.map((a) => evalExpr(a, env));
      return env.callBuiltin(expr.fn, args);
    }
  }
}

function resolveIdent(name: string, env: EvalEnv): unknown {
  // Literal identifier sugar — keep `true`/`false`/`null` working even
  // if the parser routed them through an `ident` node.
  if (name === 'true') return true;
  if (name === 'false') return false;
  if (name === 'null') return null;

  if (!TOP_LEVEL_IDENTS.has(name)) {
    if (env.strict) {
      throw new ExprEvalError(`reference to restricted identifier "${name}"`);
    }
    return undefined;
  }
  const v = env.resolveTopLevel(name);
  if (v === undefined && env.strict) {
    throw new ExprEvalError(`identifier "${name}" is not bound`);
  }
  return v;
}

function evalBinary(op: ExprBinaryOp, lhs: Expr, rhs: Expr, env: EvalEnv): unknown {
  // Short-circuit before evaluating RHS for logical operators.
  if (op === '&&') {
    const l = evalExpr(lhs, env);
    if (!truthy(l)) return l;
    return evalExpr(rhs, env);
  }
  if (op === '||') {
    const l = evalExpr(lhs, env);
    if (truthy(l)) return l;
    return evalExpr(rhs, env);
  }

  const a = evalExpr(lhs, env);
  const b = evalExpr(rhs, env);
  switch (op) {
    case '==': return looseEq(a, b);
    case '!=': return !looseEq(a, b);
    case '<':  return numericCmp('<',  a, b);
    case '<=': return numericCmp('<=', a, b);
    case '>':  return numericCmp('>',  a, b);
    case '>=': return numericCmp('>=', a, b);
    case '+':  return arith('+', a, b);
    case '-':  return arith('-', a, b);
    case '*':  return arith('*', a, b);
    case '/':  return arith('/', a, b);
    case '%':  return arith('%', a, b);
    default: {
      // Exhaustiveness — should be unreachable.
      const _exhaustive: never = op;
      void _exhaustive;
      throw new ExprEvalError(`unknown binary operator`);
    }
  }
}

/* ──────────────────────────── helpers ─────────────────────────────── */

/**
 * Convenience: evaluates an expression and coerces the result to a
 * boolean using the same rules as `if`/`while` truthiness.
 */
export function evalExprBool(expr: Expr, env: EvalEnv): boolean {
  return truthy(evalExpr(expr, env));
}

/**
 * Convenience: evaluates an expression and coerces to a string. `null`
 * and `undefined` become the empty string (never the literal "null"/
 * "undefined") so interpolation is forgiving.
 */
export function evalExprString(expr: Expr, env: EvalEnv): string {
  const v = evalExpr(expr, env);
  return v == null ? '' : String(v);
}
